import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import dayjs from 'dayjs';
import { Spin, message } from 'antd';
import { fetchMySchedule, plannedRange, statusColor, textOn, taskLabel } from './scheduleUtils';

const isNarrow = () => typeof window !== 'undefined' && window.innerWidth < 640;

// Month/week calendar: planned start→end as a spanning bar, due date as a flag chip.
const MyScheduleCalendar = ({ reloadKey, filters, onOpenTask, onUndatedCount }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const rangeRef = useRef(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (start, end) => {
    rangeRef.current = { start, end };
    setLoading(true);
    try {
      const data = await fetchMySchedule(start, end, filtersRef.current);
      // Ignore a stale response if the user already navigated elsewhere.
      if (rangeRef.current?.start !== start) return;
      setTasks(data.tasks || []);
      onUndatedCount && onUndatedCount(data.undated_count || 0, data);
    } catch {
      message.error('Failed to load your schedule');
    } finally {
      setLoading(false);
    }
  }, [onUndatedCount]);

  // Refetch the visible range after a task is edited or the filters change.
  const filtersKey = JSON.stringify(filters || {});
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; } // datesSet does the first load
    if (rangeRef.current) load(rangeRef.current.start, rangeRef.current.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, filtersKey]);

  const events = useMemo(() => {
    const out = [];
    tasks.forEach((t) => {
      const range = plannedRange(t);
      const color = statusColor(t);
      if (range) {
        out.push({
          id: `r-${t.id}`,
          title: taskLabel(t),
          start: range[0].format('YYYY-MM-DD'),
          // FullCalendar's end is exclusive, so add a day to include the last planned day.
          end: range[1].add(1, 'day').format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: color,
          borderColor: t.is_overdue ? '#cf222e' : color,
          textColor: textOn(color),
          classNames: ['ms-ev', t.is_completed ? 'is-done' : '', t.is_overdue ? 'is-overdue' : ''],
          extendedProps: { task: t, kind: 'planned' },
        });
      }
      if (t.due_date) {
        out.push({
          id: `d-${t.id}`,
          title: `🚩 ${taskLabel(t)}`,
          start: t.due_date,
          allDay: true,
          backgroundColor: t.is_completed ? '#f6f8fa' : '#ffebe9',
          borderColor: t.is_completed ? '#d0d7de' : '#cf222e',
          textColor: t.is_completed ? '#59636e' : '#cf222e',
          classNames: ['ms-ev', 'ms-due', t.is_completed ? 'is-done' : ''],
          extendedProps: { task: t, kind: 'due' },
        });
      }
    });
    return out;
  }, [tasks]);

  const renderEvent = (arg) => {
    const t = arg.event.extendedProps.task;
    const kind = arg.event.extendedProps.kind;
    const tip = [
      taskLabel(t),
      t.project_name && `Project: ${t.project_name}`,
      t.status_name && `Status: ${t.status_name}`,
      kind === 'planned' && `Planned: ${t.estimated_start_date || '—'} → ${t.estimated_end_date || '—'}`,
      t.due_date && `Due: ${t.due_date}${t.is_overdue ? ' (overdue)' : ''}`,
    ].filter(Boolean).join('\n');
    return <div className="ms-ev__text" title={tip}>{arg.event.title}</div>;
  };

  return (
    <div className="ms-calendar">
      {loading && <div className="ms-calendar__loading"><Spin size="small" /></div>}
      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView={isNarrow() ? 'dayGridWeek' : 'dayGridMonth'}
        headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek' }}
        buttonText={{ today: 'Today', month: 'Month', week: 'Week' }}
        firstDay={1}
        height="auto"
        dayMaxEvents={4}
        events={events}
        eventContent={renderEvent}
        eventClick={(info) => onOpenTask && onOpenTask(info.event.extendedProps.task.id)}
        // datesSet gives an exclusive end; the API expects inclusive dates.
        datesSet={(arg) => load(dayjs(arg.start).format('YYYY-MM-DD'), dayjs(arg.end).subtract(1, 'day').format('YYYY-MM-DD'))}
      />
    </div>
  );
};

export default MyScheduleCalendar;
